/**
 * 소싱 매입가·마진 계산 — 소싱관리 두 화면(분석/로켓그로스)이 같이 쓴다.
 *
 * 화면마다 따로 계산하면 한쪽만 고치는 사고가 난다(실제로 매입가에 밀크런비가
 * 한쪽만 빠져 있었다). 공식이 바뀌면 여기만 고친다.
 *
 * 매입가 = round((CNY × 통합통관환율 + 검수 + 바코드) × 1.1) + 예상 밀크런비
 *   - 부가세 1.1 은 통관가와 검수·바코드 모두에 붙는다(2026-09-23 주인 확정 — 예전엔 검수·바코드에 빠져 있었다).
 *   - 밀크런비는 이미 부가세 포함가라 ×1.1 을 하지 않는다.
 *   - 중국 내 운임(선택): 그 발주 전체 운임(CNY) ÷ 발주 수량 = 개당 운임을 단가에 더해 환율·검수·부가세를 같이 탄다
 *     ((구매가 + 개당 운임) × 환율 + 300) × 1.1. 안 넣으면 0 — 예전 값과 같다. 서버 도구(tool-exec/margin-calc.js, growth.sourcing.calculateMargin)와 같은 규칙이고
 *     발주 수량을 안 넣으면 2(운임 견적 기본 수량)로 나눈다.
 *   - 통합통관 공식은 쿠패스 구매요청과 같다(logisreqpurchaseV0 / server.js createQuotation1688).
 *   - 검수·바코드 단가는 중국물류비 견적 자동채움과 같은 값.
 *   - 밀크런비는 로켓그로스 입고 기준이다. 다른 입고 경로면 그만큼 보수적으로 잡히는 셈.
 */
import { estimateMilkrunCost } from './milkrunEstimate'

export const FEE_INSPECT = 200   // 검수포장비/개
export const FEE_BARCODE = 100   // 바코드비/개
export const CUSTOMS_VAT = 1.1   // 부가세: (CNY × 통합통관환율 + 검수 + 바코드) × 1.1
// 화면 분해 표시용 — 부가세 포함 검수·바코드비. 통관가(customsOnly) + 이 둘 + 밀크런 = 매입가.
export const FEE_INSPECT_VAT = Math.round(FEE_INSPECT * CUSTOMS_VAT)   // 220
export const FEE_BARCODE_VAT = Math.round(FEE_BARCODE * CUSTOMS_VAT)   // 110

/**
 * 통관가만 (부대비용 제외). 화면에서 분해를 보여줄 때 쓴다.
 * @param {number} cny 1688 구매가(CNY)
 * @param {number} rate 통합통관환율(원/元)
 * @returns {number} 원화 통관가. 입력이 없으면 0
 */
export function customsOnly (cny, rate) {
  if (!rate || !cny) return 0
  return Math.round(Number(cny) * Number(rate) * CUSTOMS_VAT)
}

/**
 * 매입가 = round((CNY × 환율 + 검수 + 바코드) × 1.1) + 밀크런.
 * 환율을 못 받았으면 null 을 준다 — 임의 환율로 틀린 마진을 보여주느니 비워 두는 게 낫다.
 * @param {number} cny 1688 구매가(CNY)
 * @param {number} rate 통합통관환율
 * @param {number} [milkrun] 개당 예상 밀크런비
 * @returns {number|null}
 */
export function purchaseCost (cny, rate, milkrun, shipCny, qty) {
  if (!rate || !cny) return null
  const unit = Number(cny) + perUnitShipCny(shipCny, qty)
  return Math.round((unit * Number(rate) + FEE_INSPECT + FEE_BARCODE) * CUSTOMS_VAT) + (Number(milkrun) || 0)
}

/**
 * 개당 중국 내 운임(CNY) = 발주 전체 운임 ÷ 발주 수량. 운임이 없거나 0 이하면 0.
 * 수량이 없으면 2 로 나눈다(서버 도구와 같은 기본값 — 운임은 2개 기준으로 견적을 받는다).
 */
export function perUnitShipCny (shipCny, qty) {
  const ship = Number(shipCny)
  if (!(ship > 0)) return 0
  const q = Number(qty)
  return ship / (Number.isInteger(q) && q >= 1 ? q : 2)
}

/**
 * 수수료 = 판매수수료(판매가×수수료율) + 풀필먼트 + 보관.
 * ff/wh 는 wing 실측값(VAT 포함)이고, 실측 전이면 0이다.
 */
export function commissionOf (판매가, 수수료율, ff, wh) {
  return Math.round((Number(판매가) || 0) * (Number(수수료율) || 0) * 0.01) +
    (Number(ff) || 0) + (Number(wh) || 0)
}

/**
 * 행 하나의 밀크런 추정치를 채운다(사이즈·카테고리 기준). 못 구하면 관련 필드를 비운다.
 * @param {object} item 보드 행 (직접 변경한다)
 */
export function applyMilkrun (item) {
  const r = estimateMilkrunCost({
    sizeX: item.sizeX, sizeY: item.sizeY, sizeZ: item.sizeZ, categoryId: item.categoryId
  })
  if (!r) {
    item.밀크런예상 = null
    item.밀크런박스 = ''
    item.밀크런적재 = null
    item.밀크런계수추정 = false
    return
  }
  item.밀크런예상 = r.perUnit
  item.밀크런박스 = r.boxLabel
  item.밀크런적재 = r.qty
  item.밀크런계수추정 = !r.coefMatched   // 카테고리 실적이 없어 폴백 계수를 쓴 경우
}

/**
 * 행의 매입가·수수료·마진·마진율·ROI·월수익을 한 번에 갱신한다.
 * 밀크런 추정은 매입가보다 먼저 돌아야 한다(매입가에 들어가므로).
 * 매입가를 모르면 마진·마진율·ROI·월수익은 0 이 아니라 null 이다(아래 주석).
 *
 * @param {object} item 보드 행 (직접 변경한다)
 * @param {number} rate 통합통관환율
 */
export function recalcRow (item, rate) {
  applyMilkrun(item)

  const 판매가 = Number(item.판매가) || 0
  item.매입가 = purchaseCost(item.구매가, rate, item.밀크런예상, item.중국내운임, item.발주수량)
  item.수수료 = commissionOf(판매가, item.수수료율, item.fullfillmentFee, item.warehouseFee)

  // 매입가를 모르면(1688 미확보·환율 미수신) 마진을 낼 수 없다. `|| 0` 으로 뭉개지 말 것 —
  // 원가를 0 으로 친 마진이 붙어 「아직 못 찾은 행」이 「대박 행」으로 보인다.
  // ROI 를 0 으로 두는 것도 같은 병이다: sourcingSignals.entryScore 가 「ROI 없음=잠정(계수 1.0)」과
  // 「ROI 0」을 갈라 쓰는데, 0 이 가면 원가를 모르는 행이 점수 0 으로 바닥에 깔린다.
  if (item.매입가 == null) {
    item.마진 = null
    item.마진율 = null
    item.ROI = null
    item.월수익 = null
    return
  }

  const 매입가 = Number(item.매입가)
  const 부가세 = (판매가 - 매입가 - item.수수료) / 11
  item.마진 = Math.round(판매가 - 매입가 - item.수수료 - 부가세)
  item.ROI = 매입가 > 0 ? Math.round(item.마진 / 매입가 * 100) : 0
  item.마진율 = 판매가 > 0 ? Math.round(item.마진 / 판매가 * 100) : 0
  // 월매출 모름(null·빈값)이면 월수익도 모름 = null — 0 이 가면 «돈 안 되는 상품» 으로 읽힌다(couponprice0-3)
  if (item.월매출 == null || item.월매출 === '') { item.월수익 = null; return }
  const 월매출 = Number(item.월매출) || 0
  item.월수익 = 판매가 > 0 ? Math.round(item.마진 * (월매출 / 판매가)) : 0
}

/**
 * 옵션 한 줄을 계산한다. 1688 옵션마다 구매가(CNY)가 달라 매입가부터 다시 잡는다.
 * 수수료율·풀필먼트·보관·밀크런은 같은 상품이라 행 값을 공유한다.
 * 행과 마찬가지로 매입가를 모르면 마진·마진율·ROI 는 null 이다.
 *
 * @param {object} item 행
 * @param {object} o 옵션 (직접 변경한다)
 * @param {number} rate 통합통관환율
 */
export function recalcOption (item, o, rate) {
  if (!o) return
  const 판매가 = Number(o.판매가) || 0
  o.매입가 = purchaseCost(o.구매가, rate, item.밀크런예상, item.중국내운임, item.발주수량)
  o.수수료 = commissionOf(판매가, item.수수료율, item.fullfillmentFee, item.warehouseFee)

  // 행과 같은 이유로 비워 둔다 — 옵션 구매가(CNY)가 아직 없는데 마진이 붙으면 안 된다.
  if (o.매입가 == null) {
    o.마진 = null
    o.마진율 = null
    o.ROI = null
    return
  }

  const 매입가 = Number(o.매입가)
  const 부가세 = (판매가 - 매입가 - o.수수료) / 11
  o.마진 = Math.round(판매가 - 매입가 - o.수수료 - 부가세)
  o.ROI = 매입가 > 0 ? Math.round(o.마진 / 매입가 * 100) : 0
  o.마진율 = 판매가 > 0 ? Math.round(o.마진 / 판매가 * 100) : 0
}

export default {
  FEE_INSPECT, FEE_BARCODE, CUSTOMS_VAT,
  customsOnly, purchaseCost, perUnitShipCny, commissionOf, applyMilkrun, recalcRow, recalcOption
}
